import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDMY } from "@/lib/date";
import { ChevronLeft, Award, Trophy, Camera, ArrowRight } from "lucide-react";

const PLACEMENT_LABEL = {
  FIRST: "1st", SECOND: "2nd", THIRD: "3rd",
  HIGHLY_COMMENDED: "HC", COMMENDED: "C", NONE: "—",
};

export default function MyEntries() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/entries");
        setData(data);
      } catch (ex) {
        setErr(ex.response?.data?.detail || ex.message);
      }
    })();
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (err) {
    return (
      <div className="min-h-screen p-12">
        <div className="ccs-card max-w-md mx-auto">
          <div className="danger-text mb-3">{String(err)}</div>
          <button onClick={doLogout} className="ccs-btn">Sign out</button>
        </div>
      </div>
    );
  }
  if (!data) return <div className="p-12 muted-text">Loading your entries…</div>;

  const { photographer, stats, history } = data;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <header
        className="px-8 py-5 flex items-center justify-between"
        style={{ borderBottom: "1px solid #1f1f1f" }}
      >
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-400">
            Camera Club Score
          </div>
          <div className="text-xl font-semibold mt-1">My Entries</div>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/portal" className="muted-text text-sm hover:text-white">
            Public portal →
          </Link>
          <div className="text-right">
            <div className="text-sm font-medium">{user?.name}</div>
            <div className="text-xs muted-text">{user?.email} · MEMBER</div>
          </div>
          <button onClick={doLogout} className="ccs-btn-ghost text-sm" data-testid="logout-button">
            Sign out
          </button>
        </div>
      </header>

      <main className="p-8 max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="ccs-label mb-2">My profile</div>
          <h1 className="text-4xl font-semibold tracking-tight">
            {photographer.display_name}
          </h1>
          <div className="mono muted-text mt-1 text-sm">
            {photographer.photographer_code}
            {photographer.membership_number ? ` · #${photographer.membership_number}` : ""}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Tile label="Total entries" value={stats.entries} />
          <Tile label="Average" value={stats.avg_score || 0} />
          <Tile label="Best" value={stats.max_score || 0} accent />
          <Tile label="Total score" value={stats.total_score || 0} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-8">
          <PlacementBox label="1st" count={stats.firsts} cls="placement-FIRST" />
          <PlacementBox label="2nd" count={stats.seconds} cls="placement-SECOND" />
          <PlacementBox label="3rd" count={stats.thirds} cls="placement-THIRD" />
          <PlacementBox label="HC" count={stats.hc} cls="placement-HIGHLY_COMMENDED" />
          <PlacementBox label="Cmd" count={stats.c} cls="placement-COMMENDED" />
        </div>

        <div className="ccs-card p-0 overflow-x-auto" data-testid="my-entries-table">
          <table className="ccs-table">
            <thead>
              <tr>
                <th>Image Title</th>
                <th>Score</th>
                <th>Place</th>
                <th>Competition</th>
                <th>Date</th>
                <th>Season</th>
              </tr>
            </thead>
            <tbody>
              {history.map((e) => (
                <tr key={e.id}>
                  <td className="font-medium">{e.image_title}</td>
                  <td className="mono text-right" style={{ paddingRight: 24 }}>{e.score}</td>
                  <td>
                    <span className={`placement-badge placement-${e.placement}`}>
                      {PLACEMENT_LABEL[e.placement] || "—"}
                    </span>
                  </td>
                  <td>{e.competition_name}</td>
                  <td className="mono muted-text">{formatDMY(e.competition_date)}</td>
                  <td className="muted-text">{e.season_name || "—"}</td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 muted-text">
                  No entries yet. Watch this space!
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div className="stat-tile">
      <div className="label">{label}</div>
      <div className="value" style={accent ? { color: "var(--accent)" } : {}}>{value}</div>
    </div>
  );
}

function PlacementBox({ label, count, cls }) {
  return (
    <div
      className="py-4 px-5 flex items-center justify-between"
      style={{ background: "#131313", border: "1px solid #1f1f1f" }}
    >
      <span className={`placement-badge ${cls}`}>{label}</span>
      <span className="mono text-2xl font-semibold">{count}</span>
    </div>
  );
}
